// @ts-nocheck
/**
 * Student Dashboard with Full Carbon Design System Implementation
 * Complete Carbon UI Shell with HeaderContainer and SideNav
 * IBM Carbon compliant implementation for student learning interface
 */

import React, { useEffect, useState } from 'react';
import {
  Grid,
  Column,
  Tile,
  ClickableTile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  TableToolbarMenu,
  TableToolbarAction,
  ProgressIndicator,
  ProgressStep,
  Tag,
  Button,
  InlineNotification,
  Modal,
  TextInput,
  TextArea,
  Form,
  FormGroup,
  Stack,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  SkeletonText,
  SkeletonPlaceholder,
  HeaderContainer,
  Header,
  HeaderName,
  HeaderGlobalBar,
  HeaderGlobalAction,
  SideNav,
  SideNavItems,
  HeaderSideNavItems,
  HeaderPanel,
  Switcher,
  SwitcherItem,
  SwitcherDivider,
} from '@carbon/react';
import {
  Dashboard,
  Education,
  Trophy,
  Bot,
  Time,
  CheckmarkFilled,
  WarningAltFilled,
  Calendar,
  ChartLineData,
  DocumentTasks,
  UserAvatar,
  NotificationNew,
  Settings,
  Logout,
  PlayFilled,
  ChevronRight,
} from '@carbon/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// TypeScript interfaces
interface Assignment {
  id: string;
  title: string;
  subject: string;
  dueDate: string;
  status: 'not_started' | 'in_progress' | 'submitted' | 'graded';
  score?: number;
  totalPoints: number;
  priority: 'low' | 'medium' | 'high';
}

interface LearningProgress {
  subject: string;
  progress: number;
  currentTopic: string;
  completedModules: number;
  totalModules: number;
  masteryLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
}

interface DashboardMetrics {
  overallProgress: number;
  currentStreak: number;
  totalPoints: number;
  completedAssignments: number;
  pendingAssignments: number;
  averageScore: number;
}

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'success' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

const StudentDashboardCarbon: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [progress, setProgress] = useState<LearningProgress[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Mock data for demonstration
      const mockAssignments: Assignment[] = [
        {
          id: '1',
          title: 'Quadratic Equations Problem Set',
          subject: 'Mathematics',
          dueDate: '2025-09-20',
          status: 'in_progress',
          score: undefined,
          totalPoints: 100,
          priority: 'high',
        },
        {
          id: '2',
          title: 'Essay: Climate Change Impact',
          subject: 'English',
          dueDate: '2025-09-22',
          status: 'not_started',
          score: undefined,
          totalPoints: 50,
          priority: 'medium',
        },
        {
          id: '3',
          title: 'Chemistry Lab Report',
          subject: 'Science',
          dueDate: '2025-09-18',
          status: 'submitted',
          score: undefined,
          totalPoints: 75,
          priority: 'high',
        },
        {
          id: '4',
          title: 'World War II Timeline',
          subject: 'History',
          dueDate: '2025-09-15',
          status: 'graded',
          score: 92,
          totalPoints: 100,
          priority: 'low',
        },
      ];

      const mockProgress: LearningProgress[] = [
        {
          subject: 'Mathematics',
          progress: 72,
          currentTopic: 'Quadratic Equations',
          completedModules: 8,
          totalModules: 12,
          masteryLevel: 'intermediate',
        },
        {
          subject: 'Science',
          progress: 85,
          currentTopic: 'Chemical Reactions',
          completedModules: 10,
          totalModules: 12,
          masteryLevel: 'advanced',
        },
        {
          subject: 'English',
          progress: 68,
          currentTopic: 'Essay Writing',
          completedModules: 7,
          totalModules: 10,
          masteryLevel: 'intermediate',
        },
        {
          subject: 'History',
          progress: 90,
          currentTopic: 'Modern History',
          completedModules: 9,
          totalModules: 10,
          masteryLevel: 'expert',
        },
      ];

      const mockMetrics: DashboardMetrics = {
        overallProgress: 78,
        currentStreak: 7,
        totalPoints: 1250,
        completedAssignments: 24,
        pendingAssignments: 3,
        averageScore: 88,
      };

      const mockNotifications: Notification[] = [
        {
          id: '1',
          type: 'info',
          title: 'New Assignment Posted',
          message: 'Physics Lab Exercise due next week',
          timestamp: '2 hours ago',
          read: false,
        },
        {
          id: '2',
          type: 'success',
          title: 'Assignment Graded',
          message: 'Your History essay has been graded: 92/100',
          timestamp: '5 hours ago',
          read: false,
        },
        {
          id: '3',
          type: 'warning',
          title: 'Assignment Due Soon',
          message: 'Chemistry Lab Report due in 2 days',
          timestamp: '1 day ago',
          read: true,
        },
      ];

      setAssignments(mockAssignments);
      setProgress(mockProgress);
      setMetrics(mockMetrics);
      setNotifications(mockNotifications);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // DataTable configuration
  const assignmentHeaders = [
    { key: 'title', header: 'Assignment' },
    { key: 'subject', header: 'Subject' },
    { key: 'dueDate', header: 'Due Date' },
    { key: 'status', header: 'Status' },
    { key: 'score', header: 'Score' },
    { key: 'actions', header: 'Actions' },
  ];

  const getAssignmentRows = () => {
    return assignments
      .filter(a => a.title.toLowerCase().includes(searchTerm.toLowerCase()))
      .map(assignment => ({
        id: assignment.id,
        title: assignment.title,
        subject: <Tag type="blue">{assignment.subject}</Tag>,
        dueDate: new Date(assignment.dueDate).toLocaleDateString(),
        status: (
          <Tag
            type={
              assignment.status === 'graded'
                ? 'green'
                : assignment.status === 'submitted'
                  ? 'cyan'
                  : assignment.status === 'in_progress'
                    ? 'warm-gray'
                    : 'red'
            }
          >
            {assignment.status.replace('_', ' ')}
          </Tag>
        ),
        score: assignment.score ? `${assignment.score}/${assignment.totalPoints}` : '-',
        actions: (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ChevronRight}
            onClick={() => {
              setSelectedAssignment(assignment);
              setShowDetailsModal(true);
            }}
            style={{ minHeight: '44px', minWidth: '44px' }}
            aria-label={`View details for ${assignment.title}`}
          >
            View
          </Button>
        ),
      }));
  };

  // Get mastery level color
  const getMasteryColor = (level: string) => {
    const colors: Record<string, string> = {
      beginner: 'blue',
      intermediate: 'cyan',
      advanced: 'green',
      expert: 'purple',
    };
    return colors[level] || 'gray';
  };

  // Get priority icon
  const getPriorityIcon = (priority: string) => {
    if (priority === 'high') return <WarningAltFilled size={16} style={{ color: '#da1e28' }} />;
    if (priority === 'medium') return <Time size={16} style={{ color: '#f1c21b' }} />;
    return <CheckmarkFilled size={16} style={{ color: '#24a148' }} />;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <Grid>
          <Column lg={16}>
            <SkeletonPlaceholder style={{ height: '200px', marginBottom: '1rem' }} />
            <SkeletonText paragraph lineCount={4} />
          </Column>
        </Grid>
      </div>
    );
  }

  return (
    <>
      {/* Carbon UI Shell */}
      <HeaderContainer
        render={({ isSideNavExpanded, onClickSideNavExpand }) => (
          <>
            <Header aria-label="Student Dashboard">
              <HeaderName href="#" prefix="Sigma08">
                Student Portal
              </HeaderName>
              <HeaderGlobalBar>
                <HeaderGlobalAction
                  aria-label="Notifications - View your notifications and alerts"
                  onClick={() => setShowNotifications(!showNotifications)}
                  isActive={showNotifications}
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  <NotificationNew size={20} />
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span
                      aria-label={`${notifications.filter(n => !n.read).length} unread notifications`}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: '#da1e28',
                        borderRadius: '50%',
                        width: '8px',
                        height: '8px',
                      }}
                    />
                  )}
                </HeaderGlobalAction>
                <HeaderGlobalAction
                  aria-label="User Settings - Access your profile settings"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  <UserAvatar size={20} />
                </HeaderGlobalAction>
                <HeaderGlobalAction
                  aria-label="Settings - Manage application settings"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  <Settings size={20} />
                </HeaderGlobalAction>
              </HeaderGlobalBar>
              <SideNav aria-label="Side navigation" expanded={isSideNavExpanded} onOverlayClick={onClickSideNavExpand}>
                <SideNavItems>
                  <HeaderSideNavItems>
                    <Switcher aria-label="My Learning">
                      <SwitcherItem aria-label="Dashboard - View your main dashboard">
                        <Dashboard size={16} /> Dashboard
                      </SwitcherItem>
                      <SwitcherDivider />
                      <SwitcherItem aria-label="Assignments - View and manage your assignments">
                        <DocumentTasks size={16} /> Assignments
                      </SwitcherItem>
                      <SwitcherItem aria-label="Progress - Track your learning progress">
                        <ChartLineData size={16} /> My Progress
                      </SwitcherItem>
                      <SwitcherItem aria-label="AI Tutor - Get help from AI tutoring assistant">
                        <Bot size={16} /> AI Tutor
                      </SwitcherItem>
                      <SwitcherItem aria-label="Achievements - View your earned badges and achievements">
                        <Trophy size={16} /> Achievements
                      </SwitcherItem>
                      <SwitcherDivider />
                      <SwitcherItem aria-label="Schedule - View your class schedule">
                        <Calendar size={16} /> Schedule
                      </SwitcherItem>
                    </Switcher>
                  </HeaderSideNavItems>
                </SideNavItems>
              </SideNav>
              {showNotifications && (
                <HeaderPanel expanded={showNotifications}>
                  <div style={{ padding: '1rem' }}>
                    <h4>Notifications</h4>
                    {notifications.map(notif => (
                      <InlineNotification
                        key={notif.id}
                        kind={notif.type}
                        title={notif.title}
                        subtitle={notif.message}
                        caption={notif.timestamp}
                        style={{ marginBottom: '0.5rem' }}
                      />
                    ))}
                  </div>
                </HeaderPanel>
              )}
            </Header>
          </>
        )}
      />

      {/* Main Dashboard Content */}
      <div style={{ padding: '2rem', marginTop: '3rem' }}>
        {/* Welcome Section */}
        <Grid>
          <Column lg={16}>
            <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Welcome back, {user?.name || 'Student'}!</h1>
            <p style={{ color: 'var(--cds-text-secondary)' }}>
              You're on a {metrics?.currentStreak} day learning streak 🔥 Keep it up!
            </p>
          </Column>
        </Grid>

        {/* Dashboard Metrics Tiles */}
        <Grid style={{ marginTop: '2rem' }}>
          <Column lg={4} md={4} sm={4}>
            <Tile>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Overall Progress</p>
                  <p style={{ fontSize: '2rem', fontWeight: 600 }}>{metrics?.overallProgress}%</p>
                </div>
                <ChartLineData size={32} style={{ color: 'var(--cds-support-info)' }} />
              </div>
            </Tile>
          </Column>
          <Column lg={4} md={4} sm={4}>
            <Tile>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Total Points</p>
                  <p style={{ fontSize: '2rem', fontWeight: 600 }}>{metrics?.totalPoints}</p>
                </div>
                <Trophy size={32} style={{ color: 'var(--cds-support-warning)' }} />
              </div>
            </Tile>
          </Column>
          <Column lg={4} md={4} sm={4}>
            <Tile>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Avg Score</p>
                  <p style={{ fontSize: '2rem', fontWeight: 600 }}>{metrics?.averageScore}%</p>
                </div>
                <Education size={32} style={{ color: 'var(--cds-support-success)' }} />
              </div>
            </Tile>
          </Column>
          <Column lg={4} md={4} sm={4}>
            <Tile>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>Pending Tasks</p>
                  <p style={{ fontSize: '2rem', fontWeight: 600 }}>{metrics?.pendingAssignments}</p>
                </div>
                <DocumentTasks size={32} style={{ color: 'var(--cds-support-error)' }} />
              </div>
            </Tile>
          </Column>
        </Grid>

        {/* Tabbed Content */}
        <div style={{ marginTop: '2rem' }}>
          <Tabs selectedIndex={activeTab} onChange={e => setActiveTab(e.selectedIndex)}>
            <TabList aria-label="Student dashboard tabs">
              <Tab>Assignments</Tab>
              <Tab>Learning Progress</Tab>
              <Tab>AI Tutor</Tab>
            </TabList>
            <TabPanels>
              {/* Assignments Tab */}
              <TabPanel>
                <DataTable rows={getAssignmentRows()} headers={assignmentHeaders}>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer title="My Assignments">
                      <TableToolbar>
                        <TableToolbarContent>
                          <TableToolbarSearch
                            placeholder="Search assignments..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                          />
                          <TableToolbarMenu>
                            <TableToolbarAction onClick={() => {}}>Filter by Subject</TableToolbarAction>
                            <TableToolbarAction onClick={() => {}}>Sort by Due Date</TableToolbarAction>
                          </TableToolbarMenu>
                          <Button
                            kind="primary"
                            renderIcon={PlayFilled}
                            style={{ minHeight: '44px' }}
                            aria-label="Start a new assignment"
                          >
                            Start New Assignment
                          </Button>
                        </TableToolbarContent>
                      </TableToolbar>
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow {...getRowProps({ row })} key={row.id}>
                              {row.cells.map(cell => (
                                <TableCell key={cell.id}>{cell.value}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </TabPanel>

              {/* Learning Progress Tab */}
              <TabPanel>
                <Grid>
                  <Column lg={16}>
                    <h3 style={{ marginBottom: '1.5rem' }}>Subject Progress</h3>
                  </Column>
                  {progress.map(subject => (
                    <Column key={subject.subject} lg={8} md={4} sm={4} style={{ marginBottom: '1.5rem' }}>
                      <Tile>
                        <div style={{ marginBottom: '1rem' }}>
                          <h4>{subject.subject}</h4>
                          <Tag type={getMasteryColor(subject.masteryLevel)}>{subject.masteryLevel}</Tag>
                        </div>
                        <ProgressIndicator value={subject.progress} label={`${subject.progress}% Complete`} />
                        <div style={{ marginTop: '1rem' }}>
                          <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                            Current Topic: {subject.currentTopic}
                          </p>
                          <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                            Modules: {subject.completedModules}/{subject.totalModules}
                          </p>
                        </div>
                        <Button
                          kind="tertiary"
                          size="sm"
                          renderIcon={PlayFilled}
                          style={{ marginTop: '1rem', minHeight: '44px' }}
                          aria-label={`Continue learning ${subject.subject}`}
                        >
                          Continue Learning
                        </Button>
                      </Tile>
                    </Column>
                  ))}
                </Grid>

                {/* Learning Path Progress Steps */}
                <div style={{ marginTop: '2rem' }}>
                  <h3 style={{ marginBottom: '1.5rem' }}>Current Learning Path</h3>
                  <ProgressIndicator currentIndex={2}>
                    <ProgressStep label="Introduction to Algebra" description="Completed on Sep 1" complete />
                    <ProgressStep label="Linear Equations" description="Completed on Sep 10" complete />
                    <ProgressStep label="Quadratic Equations" description="In Progress" current />
                    <ProgressStep label="Polynomial Functions" description="Locked" disabled />
                    <ProgressStep label="Final Assessment" description="Locked" disabled />
                  </ProgressIndicator>
                </div>
              </TabPanel>

              {/* AI Tutor Tab */}
              <TabPanel>
                <Grid>
                  <Column lg={12}>
                    <Tile style={{ height: '500px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                          <Bot size={24} style={{ marginRight: '0.5rem' }} />
                          <h3>AI Tutor Assistant</h3>
                          <Tag type="green" style={{ marginLeft: 'auto' }}>
                            Online
                          </Tag>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            overflowY: 'auto',
                            border: '1px solid var(--cds-border-subtle)',
                            borderRadius: '4px',
                            padding: '1rem',
                            marginBottom: '1rem',
                          }}
                        >
                          <div style={{ marginBottom: '1rem' }}>
                            <div
                              style={{
                                background: 'var(--cds-layer-01)',
                                padding: '0.75rem',
                                borderRadius: '4px',
                                marginBottom: '0.5rem',
                              }}
                            >
                              <p style={{ fontWeight: 500 }}>AI Tutor</p>
                              <p>
                                Hello! I'm here to help you with your studies. What would you like to work on today?
                              </p>
                            </div>
                          </div>
                        </div>
                        <Stack gap={4}>
                          <p style={{ marginBottom: '1rem', color: 'var(--cds-text-secondary)' }}>
                            Get personalized help with Socratic-method AI tutoring powered by Claude Sonnet 4.5
                          </p>
                          <Button
                            kind="primary"
                            renderIcon={ChevronRight}
                            style={{ minHeight: '48px' }}
                            onClick={() => navigate('/student/ai-tutor')}
                            aria-label="Start AI Tutoring Session"
                          >
                            Start AI Tutoring
                          </Button>
                        </Stack>
                      </div>
                    </Tile>
                  </Column>
                  <Column lg={4}>
                    <Tile style={{ marginBottom: '1rem' }}>
                      <h4 style={{ marginBottom: '1rem' }}>Quick Actions</h4>
                      <Stack gap={4}>
                        <Button
                          kind="tertiary"
                          size="sm"
                          style={{ width: '100%', minHeight: '44px' }}
                          aria-label="Open Math Problem Solver tool"
                        >
                          Math Problem Solver
                        </Button>
                        <Button
                          kind="tertiary"
                          size="sm"
                          style={{ width: '100%', minHeight: '44px' }}
                          aria-label="Open Essay Helper tool"
                        >
                          Essay Helper
                        </Button>
                        <Button
                          kind="tertiary"
                          size="sm"
                          style={{ width: '100%', minHeight: '44px' }}
                          aria-label="Open Science Lab Assistant tool"
                        >
                          Science Lab Assistant
                        </Button>
                        <Button
                          kind="tertiary"
                          size="sm"
                          style={{ width: '100%', minHeight: '44px' }}
                          aria-label="View Study Tips"
                        >
                          Study Tips
                        </Button>
                      </Stack>
                    </Tile>
                    <Tile>
                      <h4 style={{ marginBottom: '1rem' }}>Recent Sessions</h4>
                      <Stack gap={3}>
                        <div>
                          <p style={{ fontSize: '0.875rem', fontWeight: 500 }}>Quadratic Equations Help</p>
                          <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>Yesterday, 3:45 PM</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '0.875rem', fontWeight: 500 }}>Essay Writing Tips</p>
                          <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>Sep 15, 2:30 PM</p>
                        </div>
                      </Stack>
                    </Tile>
                  </Column>
                </Grid>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      </div>

      {/* Assignment Details Modal */}
      <Modal
        open={showDetailsModal}
        onRequestClose={() => setShowDetailsModal(false)}
        modalHeading="Assignment Details"
        modalLabel="Student Dashboard"
        primaryButtonText="Start Assignment"
        secondaryButtonText="Close"
        onRequestSubmit={() => {
          // Handle assignment start
          setShowDetailsModal(false);
        }}
      >
        {selectedAssignment && (
          <div>
            <h4>{selectedAssignment.title}</h4>
            <Stack gap={5} style={{ marginTop: '1rem' }}>
              <div>
                <p style={{ fontWeight: 500 }}>Subject:</p>
                <Tag type="blue">{selectedAssignment.subject}</Tag>
              </div>
              <div>
                <p style={{ fontWeight: 500 }}>Due Date:</p>
                <p>{new Date(selectedAssignment.dueDate).toLocaleDateString()}</p>
              </div>
              <div>
                <p style={{ fontWeight: 500 }}>Status:</p>
                <Tag
                  type={
                    selectedAssignment.status === 'graded'
                      ? 'green'
                      : selectedAssignment.status === 'submitted'
                        ? 'cyan'
                        : selectedAssignment.status === 'in_progress'
                          ? 'warm-gray'
                          : 'red'
                  }
                >
                  {selectedAssignment.status.replace('_', ' ')}
                </Tag>
              </div>
              <div>
                <p style={{ fontWeight: 500 }}>Priority:</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {getPriorityIcon(selectedAssignment.priority)}
                  <span>{selectedAssignment.priority}</span>
                </div>
              </div>
              {selectedAssignment.score && (
                <div>
                  <p style={{ fontWeight: 500 }}>Score:</p>
                  <p>
                    {selectedAssignment.score}/{selectedAssignment.totalPoints}
                  </p>
                </div>
              )}
            </Stack>
          </div>
        )}
      </Modal>
    </>
  );
};

export default StudentDashboardCarbon;
